import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { pageVisibility, sectionVisibility } from "@/config/pages";

const STALE = 60 * 1000;

async function fetchSetting(key: string): Promise<Record<string, boolean>> {
  const { data, error } = await supabase
    .from("site_settings")
    .select("value")
    .eq("key", key)
    .maybeSingle();

  if (error || !data) {
    // Fallback to config file defaults
    if (key === "page_visibility") return { ...pageVisibility };
    if (key === "section_visibility") return { ...sectionVisibility };
    return {};
  }

  return data.value as Record<string, boolean>;
}

export function usePageVisibility() {
  return useQuery({
    queryKey: ["site-settings", "page_visibility"],
    queryFn: () => fetchSetting("page_visibility"),
    staleTime: STALE,
  });
}

export function useSectionVisibility() {
  return useQuery({
    queryKey: ["site-settings", "section_visibility"],
    queryFn: () => fetchSetting("section_visibility"),
    staleTime: STALE,
  });
}

export function useUpdateSetting() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({ key, value }: { key: string; value: Record<string, boolean> }) => {
      const { error } = await supabase
        .from("site_settings")
        .update({ value, updated_at: new Date().toISOString() })
        .eq("key", key);
      if (error) throw error;
    },
    onSuccess: (_, { key }) => {
      qc.invalidateQueries({ queryKey: ["site-settings", key] });
    },
  });
}
